import React, { useState, useEffect } from "react";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import FormButton from "components/form/FormButton";
import TextInput from "components/form/TextInput";
import { getLanguageString as _ } from "misc/lang";
import { Paths } from "appConfig";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import {
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import EmailAssign from "../sub-organisations/EmailAssign";
import {
  apiAccountCreate,
  apiAccountDelete,
  apiGetAccountById,
  apiAccountUpdate,
  apiAssignAccountAdmin,
  apiRemoveAccountAdmin,
  apiGetAdminList,
} from "net/requests";

import cssLayout from "../layout.module.scss";
import css from "./addorganisationscreen.module.scss";

interface OrganisationFormData {
  organisationName: string;
  organisationNumber: string;
  address: string;
  secondAddress: string;
  postalCode: string;
  location: string;
  hasSubOrganisation: boolean;
  processOwnerEmail: string;
  selectedPersons: string[];
}

const AddOrganisationScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { navigate, admin, globalAppMode, account } = props;

  const isEditMode = window.location.pathname.includes("/organisation/edit/");
  const isOwnOrganisation =
    window.location.pathname === Paths.ORGANISATION && !admin?.is_super_admin;
  const organisationId = isEditMode
    ? window.location.pathname.split("/").pop()
    : isOwnOrganisation
    ? account?.uuid
    : null;

  const [formData, setFormData] = useState<OrganisationFormData>({
    organisationName: "",
    organisationNumber: "",
    address: "",
    secondAddress: "",
    postalCode: "",
    location: "",
    hasSubOrganisation: false,
    processOwnerEmail: "",
    selectedPersons: [],
  });
  const [emailError, setEmailError] = useState<string>("");
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [originalAdmins, setOriginalAdmins] = useState<
    { email: string; uuid: string }[]
  >([]);

  const handleInputChange =
    (field: keyof OrganisationFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleAddEmail = () => {
    const email = formData.processOwnerEmail.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError(_("organisation", "error_email_required"));
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError(_("organisation", "error_email_invalid"));
      return;
    }
    if (formData.selectedPersons.includes(email)) {
      setEmailError(_("organisation", "error_email_already_added"));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedPersons: [...prev.selectedPersons, email],
      processOwnerEmail: "",
    }));
    setEmailError("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedPersons: prev.selectedPersons.filter(
        (email) => email !== emailToRemove,
      ),
    }));
  };

  const handleSave = async () => {
    if (!admin) return;

    try {
      const payload = {
        type: globalAppMode.toLowerCase(),
        name: formData.organisationName,
        org_number: formData.organisationNumber,
        address_line1: formData.address,
        address_line2: formData.secondAddress,
        address_city: formData.location,
        address_region: "",
        address_postal_code: formData.postalCode,
        family_parent_signin_code: "",
        parent_uuid: null,
        // uuid: "test",
        country: "SE",
      };

      let accountUuid: string | null = null;

      if ((isEditMode || isOwnOrganisation) && organisationId) {
        // Update existing organisation
        await apiAccountUpdate(admin, organisationId, payload);
        accountUuid = organisationId;
        setNotification({
          open: true,
          message: _("organisation", "update_success"),
          severity: "success",
        });
      } else {
        // Create new organisation
        const createResp: any = await apiAccountCreate(admin, payload);
        accountUuid = createResp?.data?.uuid;
        setNotification({
          open: true,
          message: _("organisation", "create_success"),
          severity: "success",
        });
      }

      if (
        accountUuid &&
        formData.selectedPersons &&
        formData.selectedPersons.length
      ) {
        const originalEmails = originalAdmins.map((a) => a.email);
        const newEmails = formData.selectedPersons.filter(
          (e) => !originalEmails.includes(e),
        );

        if (newEmails.length) {
          const assignResults = await Promise.all(
            newEmails.map((email) =>
              apiAssignAccountAdmin(admin, accountUuid as string, email)
                .then(() => ({ email, success: true }))
                .catch((err) => ({ email, success: false, error: err })),
            ),
          );

          const failed = assignResults.filter((r: any) => !r.success);
          if (failed.length) {
            console.error("Some admin assignments failed:", failed);
            const conflictConnected = failed.some((f: any) => {
              const resp = f.error?.response?.data;
              return (
                resp?.error === "conflict" &&
                resp?.info === "user in other account"
              );
            });

            setNotification({
              open: true,
              message: conflictConnected
                ? _("organisation", "error_conflict_connected")
                : _("organisation", "create_success_but_admins_failed"),
              severity: "error",
            });
          }
        }

        // Remove admins that were present originally but are no longer in selectedPersons
        const removedAdmins = originalAdmins.filter(
          (a) => !formData.selectedPersons.includes(a.email),
        );

        if (removedAdmins.length) {
          const removeResults = await Promise.all(
            removedAdmins.map((adm) =>
              apiRemoveAccountAdmin(admin, accountUuid as string, adm.uuid)
                .then(() => ({ uuid: adm.uuid, success: true }))
                .catch((err) => ({
                  uuid: adm.uuid,
                  success: false,
                  error: err,
                })),
            ),
          );

          const failedRemovals = removeResults.filter((r: any) => !r.success);
          if (failedRemovals.length) {
            console.error("Some admin removals failed:", failedRemovals);
            setNotification({
              open: true,
              message: _("organisation", "admins_remove_failed"),
              severity: "error",
            });
          }
        }
      }
      setTimeout(() => {
        navigate(Paths.ORGANISATION);
      }, 1500);
    } catch (error) {
      console.error("Error saving organisation:", error);
      setNotification({
        open: true,
        message: _(
          "organisation",
          isEditMode || isOwnOrganisation ? "update_error" : "create_error",
        ),
        severity: "error",
      });
    }
  };
  const handleCancel = () => {
    navigate(Paths.ORGANISATION);
  };

  const handleDelete = () => {
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!admin || !organisationId) return;

    try {
      await apiAccountDelete(admin, organisationId);
      setNotification({
        open: true,
        message: _("organisation", "delete_success"),
        severity: "success",
      });
      setDeleteDialog(false);
      setTimeout(() => {
        navigate(Paths.ORGANISATION);
      }, 1500);
    } catch (error) {
      console.error("Error deleting organisation:", error);
      setNotification({
        open: true,
        message: _("organisation", "delete_error"),
        severity: "error",
      });
      setDeleteDialog(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.organisationName.trim() !== "" &&
      formData.organisationNumber.trim() !== ""
    );
  };

  useEffect(() => {
    const loadOrganisationData = async () => {
      if (!admin) return;

      // For non-super admin viewing their own organisation, use account from props
      if (isOwnOrganisation && account) {
        setFormData({
          organisationName: account.name || "",
          organisationNumber: account.org_number || "",
          address: account.address_line1 || "",
          secondAddress: account.address_line2 || "",
          postalCode: account.address_postal_code || "",
          location: account.address_city || "",
          hasSubOrganisation: false,
          processOwnerEmail: "",
          selectedPersons: [],
        });
        return;
      }

      // For super admin editing an organisation
      if (!isEditMode || !organisationId) return;

      try {
        const response = await apiGetAccountById(admin, organisationId);
        const data = response.data;

        let adminEmails: string[] = [];
        try {
          const adminsResp: any = await apiGetAdminList(admin, organisationId);
          const adminsData = adminsResp.data || [];
          adminEmails = adminsData
            .map((a: any) => a.email_address)
            .filter((e: any) => !!e);
          setOriginalAdmins(
            adminsData.map((a: any) => ({
              email: a.email_address,
              uuid: a.uuid,
            })),
          );
        } catch (err) {
          console.error("Error loading account admins:", err);
        }

        setFormData({
          organisationName: data.name || "",
          organisationNumber: data.org_number || "",
          address: data.address_line1 || "",
          secondAddress: data.address_line2 || "",
          postalCode: data.address_postal_code || "",
          location: data.address_city || "",
          hasSubOrganisation: false,
          processOwnerEmail: "",
          selectedPersons: adminEmails,
        });
      } catch (error) {
        console.error("Error loading organisation data:", error);
        setNotification({
          open: true,
          message: _("organisation", "load_error"),
          severity: "error",
        });
      }
    };

    loadOrganisationData();
  }, [isEditMode, isOwnOrganisation, organisationId, admin, account]);

  // Determine page title and subtitle based on mode and account type
  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  let pageTitle: string;
  let pageSubtitle: string;

  if (isEditMode || isOwnOrganisation) {
    pageTitle = _(
      "organisation",
      mode === "HEALTH" ? "health_edit_title" : "edit_title",
    );
    pageSubtitle = _("organisation", "edit_subtitle");
  } else {
    pageTitle = _(
      "organisation",
      mode === "HEALTH" ? "health_add_title" : "add_title",
    );
    pageSubtitle = _(
      "organisation",
      mode === "HEALTH" ? "health_add_subtitle" : "add_subtitle",
    );
  }

  const isReadOnly = !admin?.is_super_admin;

  return (
    <div className={cssLayout.layout}>
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        showDeleteButton={!!(isEditMode && admin?.is_super_admin)}
        onDelete={handleDelete}
        deleteButtonText={_("organisation", "delete_button")}
      />

      <div className={css.contentBox}>
        {/* Basic Information Section */}
        <Box className={css.section}>
          <Typography variant="h6" className={css.sectionTitle}>
            {_("organisation", "basic_information")}
          </Typography>

          <Box className={css.formGrid}>
            <TextInput
              label={_(
                "organisation",
                mode === "HEALTH"
                  ? "health_organisation_name"
                  : "organisation_name_label",
              )}
              placeholder={_(
                "organisation",
                mode === "HEALTH"
                  ? "health_placeholder_organisation_name"
                  : "placeholder_organisation_name",
              )}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.organisationName}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, organisationName: value }))
              }
              readOnly={isReadOnly}
            />

            <TextInput
              label={_(
                "organisation",
                mode === "HEALTH"
                  ? "health_organisation_number"
                  : "organisation_number_label",
              )}
              placeholder={_(
                "organisation",
                mode === "HEALTH"
                  ? "health_placeholder_organisation_number"
                  : "placeholder_organisation_number",
              )}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.organisationNumber}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, organisationNumber: value }))
              }
              readOnly={isReadOnly}
            />

            <TextInput
              label={_("organisation", "address")}
              placeholder={_("organisation", "placeholder_address")}
              labelOutside={true}
              initialValue={formData.address}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, address: value }))
              }
              readOnly={isReadOnly}
            />

            <TextInput
              label={_("organisation", "second_address")}
              placeholder={_("organisation", "placeholder_second_address")}
              labelOutside={true}
              initialValue={formData.secondAddress}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, secondAddress: value }))
              }
              readOnly={isReadOnly}
            />

            <TextInput
              label={_("organisation", "postal_code")}
              placeholder={_("organisation", "placeholder_postal_code")}
              labelOutside={true}
              initialValue={formData.postalCode}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, postalCode: value }))
              }
              readOnly={isReadOnly}
            />

            <TextInput
              label={_("organisation", "location_label")}
              placeholder={_("organisation", "placeholder_location")}
              labelOutside={true}
              initialValue={formData.location}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, location: value }))
              }
              readOnly={isReadOnly}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasSubOrganisation}
                onChange={handleInputChange("hasSubOrganisation")}
                color="primary"
                disabled={isReadOnly}
              />
            }
            label={_("organisation", "have_sub_organisation")}
            className={css.checkbox}
          />

          {formData.hasSubOrganisation && (
            <Alert severity="info" className={css.infoAlert}>
              {_("organisation", "sub_org_info")}
            </Alert>
          )}
        </Box>
      </div>

      {/* Process Owner - show only to superadmins */}
      {admin?.is_super_admin && (
        <div className={css.contentBox}>
          <EmailAssign
            title={_("organisation", "process_owner_title")}
            inputValue={formData.processOwnerEmail}
            onInputChange={(value) => {
              setFormData((prev) => ({ ...prev, processOwnerEmail: value }));
              setEmailError("");
            }}
            onAdd={handleAddEmail}
            error={emailError}
            selectedEmails={formData.selectedPersons}
            onRemove={handleRemoveEmail}
            cssModule={css}
          />
        </div>
      )}

      {/* Hide action buttons for non-superadmin */}
      {admin?.is_super_admin && (
        <Box className={css.actionButtons}>
          <FormButton
            label={_(
              "organisation",
              isEditMode || isOwnOrganisation
                ? "save_changes"
                : "add_new_organisation",
            )}
            onSubmit={handleSave}
            disabled={!isFormValid()}
            inline={true}
            variant="contained"
            color="primary"
          />
          <FormButton
            label={_("organisation", "cancel")}
            onSubmit={handleCancel}
            inline={true}
            variant="outlined"
          />
        </Box>
      )}
      <ConfirmDialog
        open={deleteDialog}
        title={_("organisation", "confirm_delete_title")}
        message={_("organisation", "confirm_delete_message")}
        itemName={formData.organisationName}
        confirmLabel={_("organisation", "confirm_delete_yes")}
        cancelLabel={_("organisation", "cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog(false)}
      />
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddOrganisationScreen;
